import type { DataTableColumn } from '@odo/shared-ui';
import ConversationStatusBadge from './ConversationStatusBadge';
import TeamSelector from './TeamSelector';
import {
    AssignedCell,
    DateCell,
    LastMessageCell,
    MarkReadUnreadCell,
    PatientCell
} from './conversationCells';
import { FrustrationCell, PriorityCell } from './ConversationTriageCells';
import { opensUpwards } from '../monitoring/shared/inboxColumnStyles';
import { FLUID_CELL_CLASS } from '../shared/fluidPill';
import { useConversationActions } from '../../hooks/useConversationActions';
import type { ConversationRow, ConversationSortBy } from '../../services/conversations';

// Compact pickers, AI marker on their corner, shrink with their column at narrow widths.
const TRIAGE_CELL_CLASS = 'pl-1.5 pr-0 py-0';
const TRIAGE_HEADER_CLASS = '!pl-1.5 !pr-0';

type Actions = ReturnType<typeof useConversationActions>;

export interface ConversationColumnOptions {
    currentUserEmail: string;
    rowCount: number;
    markingSeenKey: Actions['markingSeenKey'];
    markingUnreadKey: Actions['markingUnreadKey'];
    onMarkSeen: Actions['handleMarkAsSeen'];
    onMarkUnread: Actions['handleMarkAsUnread'];
    onTeamChange: Actions['handleTeamChange'];
    onAssign: Actions['handleAssign'];
    onUnassign: Actions['handleUnassign'];
}

/** The conversations table columns for `DataTable` - one flat, fixed set. */
export function buildConversationColumns(
    opts: ConversationColumnOptions
): DataTableColumn<ConversationRow, ConversationSortBy>[] {
    return [
        {
            key: 'patient',
            label: 'PATIENT',
            width: '18%',
            sortable: true,
            sortKey: 'name',
            cellClassName: 'pl-6 pr-0 py-0 overflow-hidden',
            headerClassName: 'pl-6 !pr-0',
            render: (row) => <PatientCell row={row} />
        },
        {
            key: 'team',
            label: 'TEAM',
            width: '12%',
            cellClassName: 'pl-2 pr-2 py-0',
            headerClassName: '!pl-2 !pr-2',
            stopClickPropagation: true,
            render: (row) => (
                <div className={FLUID_CELL_CLASS}>
                    <TeamSelector
                        team={row.team}
                        onSelect={(t) => opts.onTeamChange(row, t)}
                        fluid
                    />
                </div>
            )
        },
        {
            key: 'assigned',
            label: 'ASSIGNED',
            width: '9%',
            cellClassName: 'pl-2 pr-2 py-0',
            headerClassName: '!pl-2 !pr-2',
            stopClickPropagation: true,
            render: (row) => (
                <AssignedCell
                    row={row}
                    currentUserEmail={opts.currentUserEmail}
                    onAssign={(email) => opts.onAssign(row, email, row.assignedTo)}
                    onUnassign={() => opts.onUnassign(row, row.assignedTo)}
                />
            )
        },
        {
            key: 'priority',
            label: 'PRIORITY',
            width: '9.5%',
            sortable: true,
            sortKey: 'priority',
            cellClassName: TRIAGE_CELL_CLASS,
            headerClassName: TRIAGE_HEADER_CLASS,
            stopClickPropagation: true,
            render: (row, index) => (
                <div className={FLUID_CELL_CLASS}>
                    <PriorityCell row={row} dropUp={opensUpwards(index, opts.rowCount)} />
                </div>
            )
        },
        {
            key: 'frustration',
            label: 'FRUSTRATION',
            width: '11.5%',
            sortable: true,
            sortKey: 'frustration',
            cellClassName: TRIAGE_CELL_CLASS,
            headerClassName: TRIAGE_HEADER_CLASS,
            stopClickPropagation: true,
            render: (row, index) => (
                <div className={FLUID_CELL_CLASS}>
                    <FrustrationCell row={row} dropUp={opensUpwards(index, opts.rowCount)} />
                </div>
            )
        },
        {
            key: 'status',
            label: 'VISIBILITY',
            width: '9.5%',
            cellClassName: 'pl-2 pr-2 py-0 overflow-hidden',
            headerClassName: '!pl-2 !pr-2',
            render: (row) => (
                <div className={FLUID_CELL_CLASS}>
                    <ConversationStatusBadge status={row.status} />
                </div>
            )
        },
        {
            key: 'last-message',
            label: 'LAST MESSAGE',
            width: '10.5%',
            // px-3, not px-6: the narrow column still fits its header at 1440px.
            cellClassName: 'px-3 py-0 overflow-hidden',
            headerClassName: 'px-3',
            render: (row) => <LastMessageCell row={row} />
        },
        {
            key: 'date',
            label: 'DATE',
            width: '10%',
            sortable: true,
            sortKey: 'date',
            // px-1.5 keeps "DATE" and its sort arrow whole in a ~680px table.
            cellClassName: 'px-1.5 py-0 whitespace-nowrap overflow-hidden',
            headerClassName: 'px-1.5',
            render: (row) => <DateCell iso={row.lastMessageDate} />
        },
        {
            key: 'actions',
            // Named for the sr-only a11y header; headerNode keeps it blank visibly.
            label: 'Actions',
            headerNode: null,
            width: '10%',
            align: 'right',
            cellClassName: 'px-6 py-0 overflow-hidden',
            headerClassName: 'px-6',
            stopClickPropagation: true,
            render: (row) => (
                <MarkReadUnreadCell
                    row={row}
                    markingSeenKey={opts.markingSeenKey}
                    markingUnreadKey={opts.markingUnreadKey}
                    onMarkSeen={opts.onMarkSeen}
                    onMarkUnread={opts.onMarkUnread}
                />
            )
        }
    ];
}
